import {useRouter} from '@tanstack/react-router';
import {useCallback, useEffect, useMemo, useRef} from 'react';

import {useModelsQuery} from '~api/models.queries';
import {ImageSize} from '~utils/imageSizeCache';
import {getColorFromItems} from '~utils/miscUtils';
import {
  finetunePageActiveTabQueryParam,
  QueryStringObject,
  QueryStringValue,
  Route,
  useQueryParam,
  useQueryParams,
} from '~utils/routeUtil';
import {useConfusionCellFromQueryParams, useEvaluationCellFromQueryParams} from '~hooks/imagesOverview';
import {
  AnnotationLabel,
  ImageData,
  ImageDataAnnotation,
  ImageFilters,
  LabelItem,
  LatestAnnotations,
  PaginationActionWithTargetPage,
} from '~redux/types/images';
import {Model} from '~redux/types/models';
import {AnnotationItem, selectCurrentModel} from '~redux/reducers/annotatorReducer';
import {selectMetadata} from '~redux/reducers/filtersReducer';
import {imageDataActions} from '~redux/reducers/imageReducer';
import {selectCurrentProject} from '~redux/reducers/userReducer';
import {updateImagesData} from '~redux/actions/imageActions';
import {useAppDispatch, useAppSelector} from '~redux/index';

import {isPolygonAnnotationLabel} from '~components/images/utils/annotation.utils';
import {ImageOverviewMode} from '~components/images/utils/image-mode-mixins';
import {useLabelItems} from '~components/models/model.hooks';
import {CLASS_EMPTY, CLASS_OK, THUMBNAIL_BORDER_WIDTH, THUMBNAIL_MAX_HEIGHT, UNLABELED} from 'src/constants/constants';
import {useLabelInstances} from 'src/contexts/LabelInstancesContext';
import {useCurrentModel} from 'src/contexts/ModelContext';
import {parseMetadata} from './metadata.utils';

const FINETUNE_TEST_TAB = 'test';

export interface UseImageFiltersOptions {
  omitModelId?: boolean;
}

/**
 * Resolves whether modelId should be omitted from image filters.
 * Explicit `omitModelId` wins when passed. When omitted on `model-finetune`, derives from
 * `Route.modelFinetune` + test tab so all `useImageFilters` consumers (gallery, checked items, etc.)
 * stay consistent when switching tabs.
 */
export function resolveOmitModelIdInImageFilters({
  mode,
  omitModelId,
  pathname,
  finetunePageActiveTab,
}: {
  mode: ImageOverviewMode;
  omitModelId?: boolean;
  pathname: string;
  finetunePageActiveTab?: QueryStringValue;
}): boolean {
  if (omitModelId !== undefined) {
    return omitModelId;
  }
  if (mode !== 'model-finetune') {
    return false;
  }
  const activeTab = Array.isArray(finetunePageActiveTab)
    ? finetunePageActiveTab[0]
    : typeof finetunePageActiveTab === 'string'
      ? finetunePageActiveTab
      : undefined;
  return pathname === Route.modelFinetune && activeTab === FINETUNE_TEST_TAB;
}

export function shouldIncludeModelIdInImageFilters(mode: ImageOverviewMode, resolvedOmitModelId: boolean): boolean {
  return mode === 'model-defect-book' || (mode === 'model-finetune' && !resolvedOmitModelId);
}

/**
 * Get and update the filters applied to the image list based on the model-specific filter classes and the
 * URL query parameters. The returned update function updates the query parameters and the Redux state and reloads
 * the image list.
 *
 * @return A tuple containing:
 *         - the current imageFilter state
 *         - an update function that takes a (partial) imageFilter state,
 *           updates the query parameters and the Redux state and reloads the image list.
 */
export function useImageFilters(
  mode: ImageOverviewMode,
  initialLabels: LabelItem[] | null,
  enabled: boolean = true,
  options?: UseImageFiltersOptions,
): [
  ImageFilters | undefined,
  (
    newFilters: Partial<ImageFilters>,
    paginationActionTargetPage?: PaginationActionWithTargetPage | undefined,
  ) => Promise<void>,
] {
  const currentModel = useAppSelector(selectCurrentModel);
  const router = useRouter();
  const [finetunePageActiveTab] = useQueryParam(finetunePageActiveTabQueryParam);
  const resolvedOmitModelId = resolveOmitModelIdInImageFilters({
    mode,
    omitModelId: options?.omitModelId,
    pathname: router?.state?.location?.pathname ?? window.location.pathname,
    finetunePageActiveTab,
  });

  const dispatch = useAppDispatch();
  const [queryParamFilters, setQueryParams] = useImageFilterQueryParams();
  const {activeViewMode, isPageDirty: isLabelInstancesPageDirty} = useLabelInstances();
  const wasRefreshCalled = useRef(false);
  const confusionCell = useConfusionCellFromQueryParams();
  const meta = useAppSelector(selectMetadata);

  const initialLabelIds = useMemo((): string[] | null => {
    if (!initialLabels || initialLabels.length === 0) {
      return null;
    } else {
      return initialLabels.map((labelItem) => labelItem.id);
    }
  }, [initialLabels]);

  const updateFiltersForMode = useCallback(
    (filters: ImageFilters): ImageFilters => {
      const updatedFilters = {...filters};
      const parsedMeta = parseMetadata(meta, updatedFilters.meta);
      if (['model-finetune', 'model-defect-book'].includes(mode)) {
        if (shouldIncludeModelIdInImageFilters(mode, resolvedOmitModelId)) {
          updatedFilters['modelId'] = currentModel?.id;
        } else {
          updatedFilters['modelId'] = undefined;
        }
        // We need to filter by the model's label IDs if the user hasn't set any filter classes.
        // This ensures that we show only images belonging to the model.
        // But! Skip auto-label filter when viewing curation pipeline results, as scored images may not yet be annotated.
        const hasCurationPipelineTag = !!filters['curationPipelineTag'];
        const isClassFilterEmpty = !filters['humanAnnotationLabels'] || filters['humanAnnotationLabels'].length === 0;
        const hasExcludeHumanAnnotationLabels =
          filters['excludeHumanAnnotationLabels'] && filters['excludeHumanAnnotationLabels'].length > 0;
        if (initialLabelIds && isClassFilterEmpty && !hasExcludeHumanAnnotationLabels && !hasCurationPipelineTag) {
          updatedFilters['humanAnnotationLabels'] = [...initialLabelIds];
        }
      }

      if (filters.confusionMatrixCellID) {
        updatedFilters.confusionCell = confusionCell;
      }

      if (filters.meta) {
        if (Object.keys(parsedMeta).length > 0) {
          updatedFilters.meta = parsedMeta;
        } else {
          updatedFilters.meta = undefined;
        }
      }
      return updatedFilters;
    },
    [confusionCell, currentModel?.id, initialLabelIds, meta, mode, resolvedOmitModelId],
  );

  const currentFilters = useMemo(
    (): ImageFilters => updateFiltersForMode(queryParamFilters),
    [updateFiltersForMode, queryParamFilters],
  );

  const updateFilters = useCallback(
    (imageFilters?: Partial<ImageFilters>, paginationActionWithTargetPage?: PaginationActionWithTargetPage) => {
      const joinedFilters = {...currentFilters, ...imageFilters};

      setQueryParams(joinedFilters);
      const filtersUpdatedForMode = updateFiltersForMode(joinedFilters);

      const refreshImages = async () => {
        if (!enabled) {
          return;
        }

        await dispatch(
          updateImagesData({
            withGlobalLoading: true,
            imageFilters: filtersUpdatedForMode,
            paginationActionWithTargetPage,
          }),
        );
        dispatch(imageDataActions.resetImageIndex());
      };

      if (activeViewMode !== 'instance') {
        return refreshImages();
      }
      wasRefreshCalled.current = true;
      return Promise.resolve();
    },
    [currentFilters, setQueryParams, updateFiltersForMode, activeViewMode, dispatch, enabled],
  );

  useEffect(() => {
    if (isLabelInstancesPageDirty || (activeViewMode === 'image' && wasRefreshCalled.current)) {
      wasRefreshCalled.current = false;
      updateFilters();
    }
  }, [activeViewMode, isLabelInstancesPageDirty, updateFilters]);

  return [currentFilters, updateFilters];
}

// This defines the keys of the ImageFilter object that are used in the URL query parameters.
// We are omitting some legacy keys that are not used anymore or should not appear in the URL.
type ImageFilterQueryKeys = keyof Required<
  Omit<ImageFilters, 'isLabeledByHuman' | 'isPredictedOnlyByModel' | 'classes' | 'classesToExclude' | 'confusionCell'>
>;

// This ensures that all ImageFilter keys are present in the returned object, while allowing the values to be undefined.
type ImageFiltersWithRequiredKeysButOptionalValues = {
  [key in ImageFilterQueryKeys]: ImageFilters[key] | undefined;
};

// This object is used to enforce that all desired ImageFilter keys will be present in observedImageFilterKeys
// TypeScript will complain if a key is missing. To exclude a key, remove it from the type definition above.
const observedImageFilters: ImageFiltersWithRequiredKeysButOptionalValues = {
  automlPipelineId: undefined,
  objectId: undefined,
  objectIds: undefined,
  objectIdsToExclude: undefined,
  probability: undefined,
  tags: undefined,
  humanAnnotationLabels: undefined,
  excludeHumanAnnotationLabels: undefined,
  machinePredictionLabels: undefined,
  timeframe: undefined,
  productType: undefined,
  serialNumber: undefined,
  withNoImages: undefined,
  trainingTag: undefined,
  perspective: undefined,
  devices: undefined,
  latestLabelers: undefined,
  orderBy: undefined,
  withoutMachineDefectPredictions: undefined,
  withoutMachineObjectPredictions: undefined,
  confusionMatrixCellID: undefined,
  similaritySearch: undefined,
  evaluationCell: undefined,
  withUnstablePredictions: undefined,
  meta: undefined,
  metaRange: undefined,
  modelId: undefined,
  humanPolygonAreaSize: undefined,
  widthPx: undefined,
  heightPx: undefined,
  keypointFinetune: undefined,
  keypointEvaluation: undefined,
  keypointSelectedDistance: undefined,
  onlyDefectPredictions: undefined,
  version: undefined,
  curationPipelineTag: undefined,
};

const observedImageFilterKeys: string[] = Object.keys(observedImageFilters);

export function serializeMetadataFiltersPayload(payload: MetadataFiltersPayload): string | undefined {
  const queryParams: string[] = [];

  for (const key in payload) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const values = payload[key];
      values?.forEach((value) => {
        if (value !== null) {
          queryParams.push(`meta[${encodeURIComponent(key)}]=${encodeURIComponent(String(value))}`);
        }
      });
    }
  }

  if (queryParams.length === 0) return undefined;

  return queryParams.join('&');
}

function serializeMetadataRangePayload(payload: Record<string, {min: number; max: number}>): string | undefined {
  const queryParams: string[] = [];

  for (const key in payload) {
    if (Object.prototype.hasOwnProperty.call(payload, key)) {
      const range = payload[key];
      if (range && typeof range.min === 'number' && typeof range.max === 'number') {
        queryParams.push(`metaRange[${encodeURIComponent(key)}]=${encodeURIComponent(`${range.min}-${range.max}`)}`);
      }
    }
  }

  if (queryParams.length === 0) return undefined;

  return queryParams.join('&');
}

const parseMetadataRangePayload = (queryString: string): Record<string, {min: number; max: number}> | undefined => {
  const params = new URLSearchParams(queryString);
  const payload: Record<string, {min: number; max: number}> = {};

  params.forEach((value, key) => {
    const metaRangeMatch = key.match(/^metaRange\[(.+)\]$/);
    if (metaRangeMatch) {
      const metaKey = metaRangeMatch[1];
      const [minStr, maxStr] = value.split('-');
      const min = Number(minStr);
      const max = Number(maxStr);

      if (!isNaN(min) && !isNaN(max)) {
        payload[metaKey] = {min, max};
      }
    }
  });

  if (Object.keys(payload).length === 0) return undefined;

  return payload;
};

export const serializeEvaluationCell = (evaluationCell?: EvaluationCell): string | undefined => {
  if (!evaluationCell) return undefined;

  // predictedClass is optional (unstable-predictions view); serialize it as an empty segment instead of "undefined".
  return `${evaluationCell.areaSize}_${evaluationCell.confidence}_${evaluationCell.pipelineId}_${evaluationCell.predictedClass ?? ''}`;
};

export const serializeSimSearchFilter = (simSearchFilter?: SimSearchFilter): string | undefined => {
  if (!simSearchFilter) return undefined;

  return `${simSearchFilter.imageId}_${simSearchFilter.searchCoordinate.x}_${simSearchFilter.searchCoordinate.y}${simSearchFilter.count ? `_${simSearchFilter.count}` : ''}`;
};

/*
 * Serializes the KeypointFinetune object into a string format for URL query parameters.
 * The order is:
 * [0] datasetId
 * [1] pipelineId
 * [2] keypointLabelId
 * [3] threshold
 * [4] minDistance
 * [5] maxDistance
 * [6] selectedDistance
 * [7] withinSelectedDistance
 * [8] outsideSelectedDistance
 * [9] totalDistances
 * The values are separated by a hyphen (-).
 */
export const serializeKeypointFinetune = (keypointFinetune?: KeypointFinetune): string | undefined => {
  if (!keypointFinetune) return undefined;
  const {
    datasetId,
    pipelineId,
    keypointLabelId,
    threshold,
    minDistance = null,
    maxDistance = null,
    selectedDistance = null,
    withinSelectedDistance = null,
    outsideSelectedDistance = null,
    totalDistances,
  } = keypointFinetune;
  return `${datasetId}-${pipelineId}-${keypointLabelId}-${threshold}-${minDistance}-${maxDistance}-${selectedDistance}-${withinSelectedDistance}-${outsideSelectedDistance}-${totalDistances}`;
};

// This ensures that all ImageFilter keys are present in the returned object, but that the values are of the type defined in QueryStringObject.
type ImageFiltersWithQueryStringObjectValues = {
  [key in ImageFilterQueryKeys]: QueryStringObject[0];
};
function queryStringObjectFromImageFilters(filters: Partial<ImageFilters>): ImageFiltersWithQueryStringObjectValues {
  return {
    automlPipelineId: filters.automlPipelineId,
    humanAnnotationLabels: filters.humanAnnotationLabels,
    excludeHumanAnnotationLabels: filters.excludeHumanAnnotationLabels,
    machinePredictionLabels: filters.machinePredictionLabels,
    devices: filters.devices,
    latestLabelers: filters.latestLabelers,
    objectId: filters.objectId,
    objectIds: filters.objectIds,
    objectIdsToExclude: filters.objectIdsToExclude,
    probability: filters.probability,
    serialNumber: filters.serialNumber,
    tags: filters.tags,
    timeframe: filters.timeframe,
    trainingTag: filters.trainingTag,
    withNoImages: filters.withNoImages,
    productType: filters.productType,
    perspective: filters.perspective ? filters.perspective.map((p) => p.toString()) : undefined,
    orderBy: filters.orderBy,
    withoutMachineDefectPredictions: filters.withoutMachineDefectPredictions,
    withoutMachineObjectPredictions: filters.withoutMachineObjectPredictions,
    confusionMatrixCellID: filters.confusionMatrixCellID,
    similaritySearch: serializeSimSearchFilter(filters.similaritySearch),
    evaluationCell: serializeEvaluationCell(filters.evaluationCell),
    withUnstablePredictions: filters.withUnstablePredictions ? 'true' : undefined,
    meta: serializeMetadataFiltersPayload(filters.meta ?? {}),
    metaRange: serializeMetadataRangePayload(filters.metaRange ?? {}),
    modelId: filters.modelId,
    humanPolygonAreaSize: filters.humanPolygonAreaSize
      ? `${filters.humanPolygonAreaSize.minArea}-${filters.humanPolygonAreaSize.maxArea}`
      : undefined,
    widthPx: serializePxRange(filters.widthPx),
    heightPx: serializePxRange(filters.heightPx),
    keypointFinetune: serializeKeypointFinetune(filters.keypointFinetune),
    keypointSelectedDistance: filters.keypointSelectedDistance,
    keypointEvaluation: filters.keypointEvaluation
      ? `${filters.keypointEvaluation.pipelineId}-${filters.keypointEvaluation.keypointLabelId}`
      : undefined,
    onlyDefectPredictions: filters.onlyDefectPredictions,
    version: filters.version,
    curationPipelineTag: filters.curationPipelineTag,
  };
}

type FilterQueryStringObject = QueryStringObject & {
  [key in ImageFilterQueryKeys]?: QueryStringObject[0];
};

const parseMetadataFiltersPayload = (queryString: string): MetadataFiltersPayload | undefined => {
  const params = new URLSearchParams(queryString);
  const payload: MetadataFiltersPayload = {};

  params.forEach((value, key) => {
    const metaMatch = key.match(/^meta\[(.+)\]$/);
    if (metaMatch) {
      const metaKey = metaMatch[1];
      if (!payload[metaKey]) {
        payload[metaKey] = [];
      }
      payload[metaKey]?.push(value);
    }
  });

  if (Object.keys(payload).length === 0) return undefined;

  return payload;
};

export const parseEvaluationCell = (evaluationCell: string): EvaluationCell | undefined => {
  if (!evaluationCell) return undefined;

  const [areaSize, confidence, pipelineId, ...predictedClass] = evaluationCell.split('_');
  return {
    areaSize: Number(areaSize),
    confidence: Number(confidence),
    pipelineId,
    // Empty segment means no predicted class (unstable-predictions view).
    predictedClass: predictedClass.join('_') || undefined,
  };
};

export const parseSimSearchFilter = (simSearchFilter: string): SimSearchFilter | undefined => {
  if (!simSearchFilter) return undefined;

  const [imageId, searchCoordinateX, searchCoordinateY, count] = simSearchFilter.split('_');
  return {
    imageId,
    searchCoordinate: {
      x: Number(searchCoordinateX),
      y: Number(searchCoordinateY),
    },
    count: count ? Number(count) : undefined,
  };
};

function parseHumanPolygonAreaSize(value: string | null): {minArea: number; maxArea: number} | undefined {
  if (!value) return undefined;

  const [minArea, maxArea] = value.split('-').map(Number);
  if (isNaN(minArea) || isNaN(maxArea)) return undefined;

  return {minArea, maxArea};
}

function serializePxRange(range?: {min: number | null; max: number | null}): string | undefined {
  if (!range) return undefined;
  const min = range.min ?? 'null';
  const max = range.max ?? 'null';
  return `${min}-${max}`;
}

function parsePxRange(value: string | null): {min: number | null; max: number | null} | undefined {
  if (!value) return undefined;
  const [minStr, maxStr] = value.split('-');
  if (minStr === undefined || maxStr === undefined) return undefined;

  const min = minStr === 'null' || minStr === '' ? null : Number(minStr);
  const max = maxStr === 'null' || maxStr === '' ? null : Number(maxStr);

  if (min !== null && Number.isNaN(min)) return undefined;
  if (max !== null && Number.isNaN(max)) return undefined;
  if (min === null && max === null) return undefined;

  return {min, max};
}

function parseKeypointFinetune(value: string | null): KeypointFinetune | undefined {
  if (!value) return undefined;
  const [
    datasetId,
    pipelineId,
    keypointLabelId,
    threshold,
    minDistance,
    maxDistance,
    selectedDistance,
    withinSelectedDistance,
    outsideSelectedDistance,
    totalDistances,
  ] = value.split('-');
  return {
    datasetId: datasetId as Dataset,
    pipelineId,
    keypointLabelId,
    threshold: threshold as KeypointThreshold,
    minDistance: minDistance ? Number(minDistance) : null,
    maxDistance: maxDistance ? Number(maxDistance) : null,
    selectedDistance: selectedDistance ? Number(selectedDistance) : null,
    withinSelectedDistance: withinSelectedDistance ? Number(withinSelectedDistance) : null,
    outsideSelectedDistance: outsideSelectedDistance ? Number(outsideSelectedDistance) : null,
    totalDistances: Number(totalDistances),
  };
}

function parseKeypointEvaluation(value: string | null): KeypointEvaluation | undefined {
  if (!value) return undefined;
  const [pipelineId, keypointLabelId] = value.split('-');
  return {
    pipelineId,
    keypointLabelId,
  };
}

function imageFiltersFromQueryStringObject(filters: FilterQueryStringObject): Partial<ImageFilters> {
  const hasPerspective = filters.perspective !== undefined && filters.perspective !== null;

  let perspective: number[] | undefined = undefined;
  if (hasPerspective) {
    if (Array.isArray(filters.perspective)) {
      perspective = filters.perspective.reduce((acc: number[], curr: string | null) => {
        if (typeof curr === 'string') {
          acc.push(Number.parseInt(curr));
        }
        return acc;
      }, []);
    } else if (typeof filters.perspective === 'string') {
      perspective = [Number.parseInt(filters.perspective as string)];
    }
  }

  const imageFilters = {
    ...filters,
    objectIds: typeof filters.objectIds === 'string' ? [filters.objectIds as string] : (filters.objectIds as string[]),
    humanAnnotationLabels:
      typeof filters.humanAnnotationLabels === 'string'
        ? [filters.humanAnnotationLabels as string]
        : (filters.humanAnnotationLabels as string[]),
    excludeHumanAnnotationLabels:
      typeof filters.excludeHumanAnnotationLabels === 'string'
        ? [filters.excludeHumanAnnotationLabels as string]
        : (filters.excludeHumanAnnotationLabels as string[]),
    machinePredictionLabels:
      typeof filters.machinePredictionLabels === 'string'
        ? [filters.machinePredictionLabels as string]
        : (filters.machinePredictionLabels as string[]),
    serialNumber:
      typeof filters.serialNumber === 'string' ? [filters.serialNumber as string] : (filters.serialNumber as string[]),
    tags: typeof filters.tags === 'string' ? [filters.tags as string] : (filters.tags as string[]),
    latestLabelers:
      typeof filters.latestLabelers === 'string'
        ? [filters.latestLabelers as string]
        : (filters.latestLabelers as string[]),
    devices: typeof filters.devices === 'string' ? [filters.devices as string] : (filters.devices as string[]),
    productType:
      typeof filters.productType === 'string' ? [filters.productType as string] : (filters.productType as string[]),
    perspective,
    meta: parseMetadataFiltersPayload(filters.meta as string),
    metaRange: parseMetadataRangePayload(filters.metaRange as string),
    evaluationCell: parseEvaluationCell(filters.evaluationCell as string),
    similaritySearch: parseSimSearchFilter(filters.similaritySearch as string),
    humanPolygonAreaSize:
      typeof filters.humanPolygonAreaSize === 'string'
        ? parseHumanPolygonAreaSize(filters.humanPolygonAreaSize)
        : undefined,
    widthPx: typeof filters.widthPx === 'string' ? parsePxRange(filters.widthPx) : undefined,
    heightPx: typeof filters.heightPx === 'string' ? parsePxRange(filters.heightPx) : undefined,
    keypointFinetune: parseKeypointFinetune(filters.keypointFinetune as string),
    keypointEvaluation: parseKeypointEvaluation(filters.keypointEvaluation as string),
  } as ImageFiltersWithRequiredKeysButOptionalValues;

  // Remove undefined properties
  type ImageFilterKey = keyof typeof imageFilters;
  for (const key in imageFilters) {
    if (
      imageFilters[key as ImageFilterKey] === undefined ||
      (key === 'meta' && Object.keys(imageFilters[key] ?? {}).length === 0)
    ) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      delete imageFilters[key];
    }
  }
  return imageFilters;
}

export function useImageFilterQueryParams(): [
  Partial<ImageFilters>,
  (filters: Partial<ImageFilters> | undefined) => void,
] {
  const [queryParams, setQueryParams] = useQueryParams(observedImageFilterKeys);
  const evaluationCell = useEvaluationCellFromQueryParams();

  if (evaluationCell) {
    queryParams.evaluationCell = serializeEvaluationCell(evaluationCell);
  }

  const appliedFilters = useMemo(() => {
    return imageFiltersFromQueryStringObject(queryParams);
  }, [queryParams]);

  const updateQueryStringFilters = useCallback(
    (filters: Partial<ImageFilters> | undefined) => {
      setQueryParams(filters ? queryStringObjectFromImageFilters(filters) : {});
    },
    [setQueryParams],
  );

  return [appliedFilters, updateQueryStringFilters];
}

export function useThumbnailSize(size?: ImageSize): ImageSize {
  const currentProject = useAppSelector(selectCurrentProject);
  const dimensions = currentProject?.image?.dimensions;

  const height = size?.height || dimensions?.height || THUMBNAIL_MAX_HEIGHT;
  const width = size?.width || dimensions?.width || THUMBNAIL_MAX_HEIGHT;
  const aspectRatio = THUMBNAIL_MAX_HEIGHT / height;

  return {
    width: width * aspectRatio + THUMBNAIL_BORDER_WIDTH + THUMBNAIL_BORDER_WIDTH,
    height: THUMBNAIL_MAX_HEIGHT + THUMBNAIL_BORDER_WIDTH + THUMBNAIL_BORDER_WIDTH,
  };
}

function sortByPolygonLabelsFirst(a: AnnotationLabel, b: AnnotationLabel): number {
  if (isPolygonAnnotationLabel(a) && !isPolygonAnnotationLabel(b)) {
    return -1;
  } else if (!isPolygonAnnotationLabel(a) && isPolygonAnnotationLabel(b)) {
    return 1;
  } else {
    return 0;
  }
}

/**
 * Sorts classification labels so that OK-prefixed classes are before EMPTY-prefixed classes.
 */
function sortOKClassBeforeEMPTYClass(a: AnnotationLabel | AnnotationItem, b: AnnotationLabel | AnnotationItem): number {
  if (isPolygonAnnotationLabel(a) || isPolygonAnnotationLabel(b)) {
    return 0;
  }

  const aIsOk = a.id.startsWith(CLASS_OK);
  const bIsOk = b.id.startsWith(CLASS_OK);
  const aIsEmpty = a.id.startsWith(CLASS_EMPTY);
  const bIsEmpty = b.id.startsWith(CLASS_EMPTY);

  if (aIsOk && bIsEmpty) {
    return -1;
  } else if (aIsEmpty && bIsOk) {
    return 1;
  } else {
    return 0;
  }
}

function sortByNewestFirst(a: ImageDataAnnotation, b: ImageDataAnnotation): number {
  return b.createdAt - a.createdAt;
}

/**
 * Returns the label ID of the first label of the latest annotation for the model or the latest user annotation.
 * From the latest annotation, the annotation to get the labels from is determined as follows:
 * - Human annotations are always preferred over machine predictions, if available. If there are multiple human
 *   annotations, the newest one is used. The labels are then sorted so that OK-prefixed classes are before
 *  EMPTY-prefixed classes, and the first label ID is returned.
 * - If there are only machine predictions, we first check if they all have the same timestamp. If so, we use *all*
 *   annotations and assemble a list of labels that is then sorted so that polygon labels are sorted before class labels
 *  (polygons definitively indicate a defect or detected object). Then we sort the labels so that OK-prefixed classes are
 * before EMPTY-prefixed classes, and the first label ID is returned.
 * - If there are multiple machine predictions with different timestamps, we use the newest one and sort the labels
 * so that OK-prefixed classes are before EMPTY-prefixed classes, and the first label ID is returned.
 * - If there are no annotations, we returns null.
 */
function getThumbnailColorLabelId(
  annotations: LatestAnnotations,
  models: Model[] = [],
  modelId?: string,
): string | null {
  if (models.length === 0) {
    return null;
  }
  if (modelId) {
    const relevantAnnotation = annotations[modelId]?.latestHumanAnnotation || annotations[modelId]?.machinePrediction;
    if (!relevantAnnotation?.labels) {
      return null;
    }
    return [...relevantAnnotation.labels].sort(sortOKClassBeforeEMPTYClass)[0].id;
  }

  const [humanAnnotations, machinePredictions] = Object.values(annotations).reduce(
    ([humans, machines], annotation) => {
      const modelId = annotation.latestHumanAnnotation?.modelId || annotation.machinePrediction?.modelId;
      const model = models.find((m) => {
        return m.id === modelId;
      });

      if (annotation.latestHumanAnnotation && model && !model.archived) {
        humans.push(annotation.latestHumanAnnotation);
      }
      if (annotation.machinePrediction && model && !model.archived) {
        machines.push(annotation.machinePrediction);
      }
      return [humans, machines];
    },
    [[], []] as [ImageDataAnnotation[], ImageDataAnnotation[]],
  );

  humanAnnotations.sort(sortByNewestFirst);
  machinePredictions.sort(sortByNewestFirst);

  if (humanAnnotations.length > 0) {
    const labels = [...humanAnnotations[0].labels].sort(sortOKClassBeforeEMPTYClass);
    return labels[0].id;
  } else if (machinePredictions.length > 0) {
    // check if all machinePredictions have the same timestamp
    const allTimestampsEqual = machinePredictions.every(
      (prediction) => prediction.createdAt === machinePredictions[0].createdAt,
    );
    if (allTimestampsEqual) {
      // use labels from all models
      const allLabels = machinePredictions.flatMap((prediction) => prediction.labels);
      const labels = allLabels.sort(sortByPolygonLabelsFirst).sort(sortOKClassBeforeEMPTYClass);
      return labels[0].id;
    } else {
      // use labels from the model with the newest prediction
      const labels = [...machinePredictions[0].labels].sort(sortByPolygonLabelsFirst).sort(sortOKClassBeforeEMPTYClass);
      return labels[0].id;
    }
  }

  return null;
}

/**
 * Returns the color for the thumbnail outline based on the latest annotation for the model or the latest user annotation.
 * If no annotation is found, returns a fallback color.
 */
export function useThumbnailOutlineColor(image: ImageData): string {
  const {currentModel} = useCurrentModel();
  const projectLabels = useLabelItems({from: 'project'});
  const modelsQuery = useModelsQuery();

  return useMemo((): string => {
    const relevantLabelId = getThumbnailColorLabelId(image.annotations, modelsQuery.data, currentModel?.id);
    return getColorFromItems(relevantLabelId ?? UNLABELED, currentModel?.labels ?? projectLabels);
  }, [currentModel?.id, currentModel?.labels, image.annotations, modelsQuery.data, projectLabels]);
}

/**
 * Returns the number of **human** annotations of an image for the current model. If there is no current model, returns 0.
 */
export function useHumanAnnotationCount(image: ImageData): number {
  const {currentModel} = useCurrentModel();
  if (!currentModel) {
    return 0;
  }
  return image.annotations[currentModel.id]?.latestHumanAnnotation?.labels.length || 0;
}
